import React, { createContext, useContext, useMemo } from 'react';
import { useAuth } from '@/hooks/useAuth';

// Define the context type with offline authentication
interface UserContextType {
  // Identity - always available if authenticated
  user: { id: string; email?: string } | null;
  
  // Profile - may be null initially, loaded from Supabase
  profile: any | null;
  employee: any | null;
  
  // Auth state
  loading: boolean;
  error: string | null;
  isOffline: boolean;
  isOfflineMode: boolean;
  needsPinSetup: boolean;
  isAuthenticated: boolean;
  authSource: 'supabase' | 'offline' | null;
  
  // Profile state
  profileLoading: boolean;
  profileStale: boolean;
  
  login: (email: string, password: string) => Promise<{
    success: boolean;
    error?: string;
  }>;
  
  logout: () => Promise<boolean>;
  
  validateOfflinePin: (pin: string) => Promise<{
    success: boolean;
    error?: string;
  }>;
  
  setupPin: (pin: string) => Promise<{
    success: boolean;
    error?: string;
  }>;
  
  resetPin: () => void;
}

// Create context with initial value
const UserContext = createContext<UserContextType>({
  user: null,
  profile: null,
  employee: null,
  loading: true,
  error: null,
  isOffline: false,
  isOfflineMode: false,
  needsPinSetup: false,
  isAuthenticated: false,
  authSource: null,
  profileLoading: false,
  profileStale: false,
  login: async () => ({ success: false }),
  logout: async () => false,
  validateOfflinePin: async () => ({ success: false }),
  setupPin: async () => ({ success: false }),
  resetPin: () => {}
});

export const UserProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const auth = useAuth();

  const value = useMemo(() => ({
    // Identity
    user: auth.user,
    isAuthenticated: auth.isAuthenticated,
    authSource: auth.authSource,
    
    // Profile from PowerSync
    profile: auth.profile,
    employee: auth.employee,
    profileLoading: auth.profileLoading,
    profileStale: auth.profileStale,
    
    // Auth state
    loading: auth.loading,
    error: auth.error,
    isOffline: auth.isOffline,
    isOfflineMode: auth.isOfflineMode,
    needsPinSetup: auth.needsPinSetup,
    
    // Methods
    login: auth.login,
    logout: auth.logout,
    validateOfflinePin: auth.validateOfflinePin,
    setupPin: auth.setupPin,
    resetPin: auth.resetPin
  }), [
    auth.user, auth.isAuthenticated, auth.authSource,
    auth.profile, auth.employee, auth.profileLoading, auth.profileStale,
    auth.loading, auth.error, auth.isOffline, auth.isOfflineMode, auth.needsPinSetup,
    auth.login, auth.logout, auth.validateOfflinePin, auth.setupPin, auth.resetPin
  ]);

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};